import { Button, Typography } from "antd";
import styled, { keyframes } from "styled-components";

const { Title, Text } = Typography;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const PageWrapper = styled.div`
  max-width: 920px;
  margin: 0 auto;
  padding: 0 16px;
`;

// ─── Hero ────────────────────────────────────────────────────────────

export const HeroSection = styled.section`
  text-align: center;
  padding: 32px 0 24px;
`;

export const Eyebrow = styled(Text)`
  &.ant-typography {
    display: inline-block;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    font-weight: 700;
    font-size: 11px;
    color: #D81B60;
    background: #FDF0F4;
    padding: 6px 14px;
    border-radius: 999px;
    margin-bottom: 16px;
  }
`;

export const HeroTitle = styled(Title).attrs({ level: 1 })`
  &.ant-typography {
    font-size: clamp(1.7em, 4vw, 2.2em);
    line-height: 1.2;
    margin: 0 0 14px;
    color: #1e293b;
    font-weight: 800;
  }
`;

export const HeroSubtitle = styled(Text)`
  &.ant-typography {
    display: block;
    font-size: 15px;
    color: #475569;
    line-height: 1.6;
    max-width: 640px;
    margin: 0 auto 24px;
  }
`;

export const HeroCtaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
`;

export const PrimaryCta = styled(Button)`
  &.ant-btn {
    border-radius: 14px;
    height: 46px;
    padding: 0 24px;
    font-weight: 600;
    background: linear-gradient(135deg, #D81B60, #FF7F6B);
    border: none;
    box-shadow: 0 4px 14px rgba(216, 27, 96, 0.32);
    color: #fff;
  }
  &.ant-btn:hover,
  &.ant-btn:focus {
    background: linear-gradient(135deg, #B0124A, #E64A5E) !important;
    color: #fff !important;
  }
`;

export const SecondaryCta = styled(Button)`
  &.ant-btn {
    border-radius: 14px;
    height: 46px;
    padding: 0 22px;
    font-weight: 600;
    background: transparent;
    border: 1.5px solid #FBD9E3;
    color: #475569;
  }
  &.ant-btn:hover,
  &.ant-btn:focus {
    border-color: #D81B60 !important;
    color: #D81B60 !important;
    background: transparent !important;
  }
`;

// ─── Intro paragraph ─────────────────────────────────────────────────

export const IntroSection = styled.section`
  margin: 32px auto;
  max-width: 720px;
  padding: 0 8px;
`;

export const IntroHeading = styled(Title).attrs({ level: 2 })`
  &.ant-typography {
    font-size: 1.35em;
    color: #1e293b;
    margin-bottom: 14px;
  }
`;

export const IntroParagraph = styled(Text)`
  &.ant-typography {
    display: block;
    font-size: 14.5px;
    color: #475569;
    line-height: 1.75;
    margin-bottom: 12px;
  }
`;

// ─── Section heading ─────────────────────────────────────────────────

export const SectionHeading = styled(Title).attrs({ level: 2 })`
  &.ant-typography {
    text-align: center;
    font-size: 1.4em;
    color: #1e293b;
    margin: 36px auto 22px;
  }
`;

// ─── Pain ────────────────────────────────────────────────────────────

export const PainGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
  margin: 0 auto 24px;
`;

export const PainCard = styled.div`
  background: #fff;
  border: 1.5px solid #fee2e2;
  border-radius: 18px;
  padding: 22px 20px;
  text-align: center;
  box-shadow: 0 2px 12px rgba(239, 68, 68, 0.06);
  animation: ${fadeInUp} 0.4s ease-out forwards;
`;

export const PainEmoji = styled.div`
  font-size: 36px;
  margin-bottom: 10px;
`;

export const PainTitle = styled(Text)`
  &.ant-typography {
    display: block;
    font-weight: 700;
    font-size: 15px;
    color: #1e293b;
    margin-bottom: 8px;
  }
`;

export const PainText = styled(Text)`
  &.ant-typography {
    font-size: 13.5px;
    color: #64748b;
    line-height: 1.6;
  }
`;

// ─── Features ────────────────────────────────────────────────────────

export const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 14px;
  margin: 0 auto 24px;
`;

export const FeatureCard = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  background: #fff;
  border: 1.5px solid #FDF0F4;
  border-radius: 16px;
  padding: 16px 18px;
  box-shadow: 0 2px 8px rgba(216, 27, 96, 0.06);
`;

export const FeatureCheck = styled.div`
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #FDF0F4;
  color: #D81B60;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 800;
  margin-top: 2px;
`;

export const FeatureBody = styled.div`
  flex: 1;
`;

export const FeatureTitle = styled(Text)`
  &.ant-typography {
    display: block;
    font-weight: 700;
    font-size: 14.5px;
    color: #1e293b;
    margin-bottom: 4px;
  }
`;

export const FeatureText = styled(Text)`
  &.ant-typography {
    font-size: 13.5px;
    color: #64748b;
    line-height: 1.55;
  }
`;

// ─── Testimonials ────────────────────────────────────────────────────

export const TestimonialGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 14px;
  margin: 0 auto 24px;
`;

export const TestimonialCard = styled.div`
  background: #fff;
  border: 1.5px solid #FDF0F4;
  border-radius: 18px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(216, 27, 96, 0.06);
`;

export const TestimonialQuote = styled(Text)`
  &.ant-typography {
    display: block;
    font-size: 14px;
    color: #475569;
    line-height: 1.7;
    font-style: italic;
    margin-bottom: 12px;
  }

  &.ant-typography::before {
    content: "“";
    font-size: 28px;
    color: #D81B60;
    line-height: 0;
    vertical-align: -8px;
    margin-right: 4px;
  }
`;

export const TestimonialAttr = styled(Text)`
  &.ant-typography {
    font-size: 13px;
    color: #1e293b;
    font-weight: 600;
  }
`;

export const TestimonialMeta = styled(Text)`
  &.ant-typography {
    font-size: 12px;
    color: #94a3b8;
    margin-left: 6px;
  }
`;

// ─── FAQ ─────────────────────────────────────────────────────────────

export const FaqWrapper = styled.div`
  max-width: 720px;
  margin: 0 auto 24px;

  .ant-collapse {
    background: transparent;
    border: none;
  }

  .ant-collapse-item {
    border-bottom: 1px solid #FDF0F4 !important;
    border-radius: 0 !important;
  }

  .ant-collapse-header {
    font-weight: 600 !important;
    font-size: 14.5px !important;
    color: #1e293b !important;
    padding: 16px 0 !important;
  }

  .ant-collapse-content-box {
    padding: 0 0 16px !important;
    color: #475569;
    font-size: 14px;
    line-height: 1.7;
  }
`;

// ─── CTA Block ───────────────────────────────────────────────────────

export const CtaBlock = styled.section`
  text-align: center;
  margin: 36px auto 24px;
  padding: 28px 24px;
  background: linear-gradient(135deg, #FDF0F4, #fdf4ff);
  border-radius: 22px;
  max-width: 720px;
`;

export const CtaTitle = styled(Title).attrs({ level: 2 })`
  &.ant-typography {
    font-size: 1.3em;
    color: #1e293b;
    margin-bottom: 8px;
  }
`;

export const CtaBody = styled(Text)`
  &.ant-typography {
    display: block;
    font-size: 14px;
    color: #475569;
    line-height: 1.6;
    margin-bottom: 18px;
    max-width: 540px;
    margin-left: auto;
    margin-right: auto;
  }
`;

export const Divider = styled.div`
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    #FBD9E3 30%,
    #FBD9E3 70%,
    transparent
  );
  max-width: 600px;
  margin: 28px auto;
`;

const Styles = {
  PageWrapper,
  HeroSection,
  Eyebrow,
  HeroTitle,
  HeroSubtitle,
  HeroCtaRow,
  PrimaryCta,
  SecondaryCta,
  IntroSection,
  IntroHeading,
  IntroParagraph,
  SectionHeading,
  PainGrid,
  PainCard,
  PainEmoji,
  PainTitle,
  PainText,
  FeatureGrid,
  FeatureCard,
  FeatureCheck,
  FeatureBody,
  FeatureTitle,
  FeatureText,
  TestimonialGrid,
  TestimonialCard,
  TestimonialQuote,
  TestimonialAttr,
  TestimonialMeta,
  FaqWrapper,
  CtaBlock,
  CtaTitle,
  CtaBody,
  Divider,
};

export default Styles;
